import React, { useState, useEffect } from 'react';
import { FiAward, FiFilter, FiPlus, FiEdit, FiTrash2, FiX, FiCheckCircle, FiSearch } from 'react-icons/fi';
import userSkillsService from '../../services/userSkillsService';
import './ResourceAllocationList.css';
import userService from '../../services/userService';
import { toast } from 'react-toastify';
import CommonModal from '../../services/CommonModal';

const UserSkillsList = () => {
  const [skills, setSkills] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [users, setUsers] = useState([]);
  const [isChecked, setIsChecked] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState(null);

  const [formData, setFormData] = useState({
    user_id: '',
    skill_name: '',
    proficiency_level: '',
    years_experience: '',
    certified: false,
    last_used_date: '',
    certificateName: ''
  });
  const [filters, setFilters] = useState({
    skill_name: '',
    proficiency_level: '',
    certified: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  const confirmDelete = () => {
    handleDelete(selectedSkill.id);
    setShowDeleteModal(false);
    setSelectedSkill(null);
  };

  useEffect(() => {
    loadSkills();
    loadStatistics();
    loadUsers()
  }, [filters, pagination.page]);

  const loadUsers = async () => {
    try {
      const usersData = await userService.getForDropdown();
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    }
  };

  const loadSkills = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) delete params[key];
      });

      const data = await userSkillsService.getAll(params);
      setSkills(data.data || []);
      setPagination(prev => ({
        ...prev,
        total: data.total || 0,
        pages: data.pages || 0
      }));
    } catch (error) {
      console.error('Error loading skills:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const data = await userSkillsService.getStatistics();
      setStatistics(data.data);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({
      skill_name: '',
      proficiency_level: '',
      certified: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDelete = async (id) => {
    // if (!window.confirm('Are you sure you want to delete this skill?')) return;

    try {
      await userSkillsService.delete(id);
      loadSkills();
      loadStatistics();
    } catch (error) {
      console.error('Error deleting skill:', error);
      alert('Failed to delete skill');
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleOpenModal = (skill = null) => {
    if (skill) {
      setEditingSkill(skill);
      setFormData({
        user_id: skill.user_id || '',
        skill_name: skill.skill_name || '',
        proficiency_level: skill.proficiency_level || '',
        years_experience: skill.years_experience || '',
        certified: skill.certified || false,
        last_used_date: skill.last_used_date ? skill.last_used_date.split('T')[0] : ''
      });
    } else {
      setEditingSkill(null);
      setFormData({
        user_id: '',
        skill_name: '',
        proficiency_level: '',
        years_experience: '',
        certified: false,
        last_used_date: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSkill(null);
    setFormData({
      user_id: '',
      skill_name: '',
      proficiency_level: '',
      years_experience: '',
      certified: false,
      last_used_date: ''
    });
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingSkill) {
        await userSkillsService.update(editingSkill.id, formData);
      } else {
        await userSkillsService.create(formData);
      }
      handleCloseModal();
      loadSkills();
      loadStatistics();
    } catch (error) {
      console.error('Error saving skill:', error);
      toast.error(error.response?.data?.message || 'Failed to save skill');
    }
  };

  return (
    <div className="resource-allocation-list">

      <CommonModal
        isOpen={showDeleteModal}
        title="Delete Skill"
        message="Are you sure you want to delete this skill?"
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedSkill(null);
        }}
        onConfirm={confirmDelete}
        type="error"
        confirmText="Delete"
        cancelText="Cancel"
      />

      {statistics && (
        <div className="statistics-cards">
          <div className="stat-card">
            <div className="stat-icon">
              <FiAward />
            </div>
            <div className="stat-content">
              <div className="stat-value">{statistics.total_skills || 0}</div>
              <div className="stat-label">Total Skills</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon success">
              <FiCheckCircle />
            </div>
            <div className="stat-content">
              <div className="stat-value">{statistics.certified_skills || 0}</div>
              <div className="stat-label">Certified Skills</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon info">
              <FiAward />
            </div>
            <div className="stat-content">
              <div className="stat-value">{statistics.by_proficiency?.Expert || 0}</div>
              <div className="stat-label">Expert Level</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon warning">
              <FiAward />
            </div>
            <div className="stat-content">
              <div className="stat-value">{statistics.average_years_experience || 0}</div>
              <div className="stat-label">Avg Years Exp</div>
            </div>
          </div>
        </div>
      )}

      <div className="list-header">
        <div className="header-left">
          <h2>Skills Matrix</h2>
          <span className="count">{pagination.total} total</span>
        </div>
        <div className="header-right">
          <button className="btn btn-primary" onClick={() => handleOpenModal()}>
            <FiPlus /> Add Skill
          </button>
          <button className="btn-icon" onClick={() => setShowFilters(!showFilters)}>
            <FiFilter />
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="filters-panel">
          <div className="filters-grid">
            <div className="form-group">
              <label>Skill Name</label>
              <input
                type="text"
                name="skill_name"
                value={filters.skill_name}
                onChange={handleFilterChange}
                placeholder="Search skills..."
              />
            </div>
            <div className="form-group">
              <label>Proficiency Level</label>
              <select name="proficiency_level" value={filters.proficiency_level} onChange={handleFilterChange}>
                <option value="">All Levels</option>
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
                <option value="Expert">Expert</option>
              </select>
            </div>
            <div className="form-group">
              <label>Certification</label>
              <select name="certified" value={filters.certified} onChange={handleFilterChange}>
                <option value="">All</option>
                <option value="true">Certified</option>
                <option value="false">Not Certified</option>
              </select>
            </div>
          </div>
          <div className="filters-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>
              <FiX /> Clear Filters
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading skills...</div>
      ) : skills.length === 0 ? (
        <div className="empty-state">
          <FiAward size={48} />
          <h3>No Skills Found</h3>
          <p>No skills match your current filters</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Skill Name</th>
                  <th>Proficiency</th>
                  <th>Experience</th>
                  <th>Certified</th>
                  <th>Last Used</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {skills.map(skill => (
                  <tr key={skill.id}>
                    <td>
                      <div className="user-cell">
                        <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                                                      {}
                            {skill.user?.avatar_url ? (
                              <img
                                src={skill.user.avatar_url}
                                alt={`${skill.user.first_name} ${skill.user.last_name}`}
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              <>
                                {skill.user?.first_name?.[0]}
                                {skill.user?.last_name?.[0]}
                              </>
                            )}
                        </div>
                        <div>
                          <div className="user-name">
                            {skill.user?.first_name} {skill.user?.last_name}
                          </div>
                          <div className="user-email">{skill.user?.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <strong>{skill.skill_name}</strong>
                    </td>
                    <td>
                      <span className={`status-badge ${userSkillsService.getProficiencyColor(skill.proficiency_level)}`}>
                        {skill.proficiency_level}
                      </span>
                    </td>
                    <td>{userSkillsService.formatYearsExperience(skill.years_experience)}</td>
                    <td>
                      {skill.certified ? (
                        <span className="status-badge success badge-success">
                          <FiCheckCircle /> Certified
                        </span>
                      ) : (
                        <span className="status-badge secondary badge-secondary">Not Certified</span>
                      )}
                    </td>
                    <td>
                      {skill.last_used_date
                        ? new Date(skill.last_used_date).toLocaleDateString()
                        : 'N/A'
                      }
                    </td>
                    <td>
                      <div className="flex gap-2 justify-start">
                        <button
                          className="btn-icon btn-icon-primary"
                          onClick={() => handleOpenModal(skill)}
                          title="Edit"
                        >
                          <FiEdit className='text-blue-600 hover:text-blue-900' />
                        </button>
                        <button
                          className="btn-icon btn-icon-danger"
                          onClick={() => {
                          setSelectedSkill(skill);
                          setShowDeleteModal(true);
                        }}
                          title="Delete"
                        >
                          <FiTrash2 className='text-red-600 hover:text-red-900' />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-secondary"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                Previous
              </button>
              <span className="page-info">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                className="btn btn-secondary"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSkill ? 'Edit Skill' : 'Add New Skill'}</h2>
              <button className="btn-icon" onClick={handleCloseModal}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* <div className="form-group">
                  <label>User ID *</label>
                  <input
                    type="number"
                    name="user_id"
                    value={formData.user_id}
                    onChange={handleFormChange}
                    required
                    placeholder="Enter user ID"
                  />
                </div> */}
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6'>
                  <div className="form-group">
                    <label>User ID*</label>
                    <select
                      name="user_id"
                      value={formData.user_id}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Select User</option>
                      {users.map(user => (
                        <option key={user.value} value={user.value}>
                          {user.label} ({user.email})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Skill Name *</label>
                    <input
                      type="text"
                      name="skill_name"
                      value={formData.skill_name}
                      onChange={handleFormChange}
                      required
                      placeholder="e.g., JavaScript, Project Management"
                    />
                  </div>
                </div>
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
                  <div className="form-group">
                    <label>Proficiency Level *</label>
                    <select
                      name="proficiency_level"
                      value={formData.proficiency_level}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Select Level</option>
                      <option value="Beginner">Beginner</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Advanced">Advanced</option>
                      <option value="Expert">Expert</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Years of Experience</label>
                    <input
                      type="number"
                      name="years_experience"
                      value={formData.years_experience}
                      onChange={handleFormChange}
                      min="0"
                      max="50"
                      step="0.5"
                      placeholder="e.g., 3.5"
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Used Date</label>
                    <input
                      type="date"
                      name="last_used_date"
                      value={formData.last_used_date}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>
                <div className='flex gap-4'>
                  <div className="checkbox-group w-1/3">
                    <label>
                      <input
                        type="checkbox"
                        name="certified"
                        checked={formData.certified}
                        onChange={handleFormChange}
                      />
                      <span>Certified</span>
                    </label>
                  </div>
                  <div className='w-100'>
                    {formData.certified && (
                      <div className='form-group'>
                        <label>Digital Link</label>
                        <input
                          type="text"
                          name="certificateName"
                          value={formData.certificateName || ""}
                          onChange={handleFormChange}
                          placeholder="https://www.credly.com/badges/12345678-90ab-cdef-1234-567890abcdef"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingSkill ? 'Update Skill' : 'Add Skill'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserSkillsList;

// ---